/* assets/cart-gift.js — Free gift progress + leaner cart sync (3 tiers) */
(function () {
  // ---------- tiny helpers ----------
  const $  = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const debounce = (fn, ms = 140) => { let t; return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); }; };

  let _lastTierStage = null;     // 0,1,2,3
  let _domRefreshLock = false;   // prevents loops while we refresh
  let _internalWrite = false;    // FIX #2: prevents interceptor from catching our own cart writes
  let _reloadScheduled = false;  // FIX #5: prevents repeat page reloads

  // 1) Deduplicate /cart.js reads (coalesce concurrent calls)
  let _cartInflight = null;
  async function fetchCart() {
    if (_cartInflight) return _cartInflight;
    _cartInflight = fetch('/cart.js', { headers: { 'Accept': 'application/json' }})
      .then(r => r.json())
      .finally(() => { _cartInflight = null; });
    return _cartInflight;
  }

  // FIX #2: All internal cart writes go through this so the interceptor ignores them
  async function internalCartFetch(url, body) {
    _internalWrite = true;
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      return res;
    } finally {
      _internalWrite = false;
    }
  }

  // --- UI busy state for gift add ---
  let giftAddLock = false;
  let lockedBtn   = null;
  let giftsBusy   = false;

  function setGiftsBusy(isBusy, exceptBtn = null) {
    const ctx = getContext();
    if (!ctx) return;

    ctx.giftsWrap.dataset.busy = isBusy ? 'true' : 'false';

    if (exceptBtn) {
      if (isBusy) exceptBtn.setAttribute('data-busy-exempt', 'true');
      else        exceptBtn.removeAttribute('data-busy-exempt');
    } else {
      $$('[data-add-gift][data-busy-exempt]', ctx.giftsWrap).forEach(b => b.removeAttribute('data-busy-exempt'));
    }

    $$('[data-add-gift]', ctx.giftsWrap).forEach(b => {
      if (exceptBtn && b === exceptBtn) return;
      b.disabled = !!isBusy;
      b.classList.toggle('gift-is-disabled', !!isBusy);
      if (isBusy) b.setAttribute('aria-disabled', 'true');
      else        b.removeAttribute('aria-disabled');
    });
  }

  function applyBusyState(root = document) {
    const wrap = root.querySelector('.quick-cart [data-gifts]');
    if (!wrap) return;

    if (giftsBusy) {
      wrap.setAttribute('data-busy', 'true');
      wrap.querySelectorAll('[data-add-gift]').forEach(b => {
        b.disabled = true;
        b.classList.add('gift-is-disabled');
        b.setAttribute('aria-disabled', 'true');
      });
    } else {
      wrap.removeAttribute('data-busy');
    }
  }

  /** Ensure total gift qty never exceeds ctx.maxGifts (safety net) */
  async function trimGiftsToMax(cart, ctx) {
    const state = calcFromCart(cart, ctx);

    // Detect tier change and refresh DOM (now 3 tiers)
    const stageNow = state.tier3 ? 3 : (state.tier2 ? 2 : (state.tier1 ? 1 : 0));
    if (_lastTierStage !== null && stageNow !== _lastTierStage && !_domRefreshLock) {
      try {
        _domRefreshLock = true;
        await refreshQuickCartDOM();
      } finally {
        _domRefreshLock = false;
      }
    }
    _lastTierStage = stageNow;

    const max = state.liveMax || 0;
    const items = (cart && cart.items) || [];

    let totalGift = 0;
    items.forEach(i => { if (i?.properties?._gift === 'true') totalGift += i.quantity; });

    if (totalGift > 0) {
      await setGiftMaxAttribute(max);
    } else {
      await setGiftMaxAttribute(0);
    }

    if (totalGift <= max) return false;

    let remaining = max;
    for (const i of items) {
      if (i?.properties?._gift === 'true') {
        const newQty = Math.max(0, Math.min(i.quantity, remaining));
        remaining -= newQty;
        if (newQty !== i.quantity) {
          // FIX #2: use internalCartFetch so the interceptor doesn't re-trigger
          await internalCartFetch('/cart/change.js', { id: i.key, quantity: newQty });
        }
      }
    }
    return true;
  }

  // 4) Guarded carousel init (don't bind listeners repeatedly)
  function initGiftCarousel() {
    const root = document.querySelector('.quick-cart');
    if (!root) return;

    document.querySelectorAll('.cart-gifts').forEach(g => {
      if (g.dataset.carouselInit === '1') return;
      g.dataset.carouselInit = '1';

      const vp    = g.querySelector('[data-gifts-viewport]');
      const prev  = g.querySelector('.cart-gifts__nav--prev');
      const next  = g.querySelector('.cart-gifts__nav--next');
      const track = g.querySelector('.cart-gifts__track');
      const card  = g.querySelector('.cart-gifts__item');
      if (!vp || !prev || !next || !track) return;

      const gapPx = () => {
        const cs = getComputedStyle(track);
        return parseFloat(cs.columnGap || cs.gap) || 12;
      };
      const cardStep = () => {
        const w = card ? card.getBoundingClientRect().width : 0;
        return w ? w + gapPx() : Math.max(240, Math.floor(vp.clientWidth * 0.9));
      };

      const updateArrows = () => {
        const maxScroll = vp.scrollWidth - vp.clientWidth - 1;
        prev.disabled = vp.scrollLeft <= 0;
        next.disabled = vp.scrollLeft >= maxScroll;
      };

      prev.addEventListener('click', () => {
        vp.scrollBy({ left: -cardStep(), behavior: 'smooth' });
        setTimeout(updateArrows, 200);
      });
      next.addEventListener('click', () => {
        vp.scrollBy({ left:  cardStep(), behavior: 'smooth' });
        setTimeout(updateArrows, 200);
      });

      vp.addEventListener('scroll', () => requestAnimationFrame(updateArrows));
      window.addEventListener('resize', () => setTimeout(updateArrows, 50), { passive: true });

      updateArrows();
    });
  }

  function formatMoneyIntl(cents, currencyCode) {
    try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode }).format(cents / 100); }
    catch { return (cents / 100).toFixed(2); }
  }

  function getContext() {
    const root = document.querySelector('.quick-cart');
    if (!root) return null;
    const giftsWrap = root.querySelector('[data-gifts]');
    const labelEl   = root.querySelector('[data-progress-label]');
    const fillEl    = root.querySelector('[data-progress-fill]');
    if (!giftsWrap || !labelEl || !fillEl) return null;

    const shipThresholdCents = Math.round(parseFloat(giftsWrap.dataset.thresholdShip || '0') * 100);
    const g1Cents            = Math.round(parseFloat(giftsWrap.dataset.thresholdG1   || '0') * 100);
    const g2Cents            = Math.round(parseFloat(giftsWrap.dataset.thresholdG2   || '0') * 100);
    const g3Cents            = Math.round(parseFloat(giftsWrap.dataset.thresholdG3   || '0') * 100);
    const max1               = parseInt(giftsWrap.dataset.max1 || '1', 10);
    const max2               = parseInt(giftsWrap.dataset.max2 || '2', 10);
    const max3               = parseInt(giftsWrap.dataset.max3 || '3', 10);
    const nextdayCents       = Math.round(parseFloat(giftsWrap.dataset.thresholdNextday || '0') * 100);

    return {
      root, giftsWrap, labelEl, fillEl,
      shipThresholdCents,
      gift1ThresholdCents: g1Cents,
      gift2ThresholdCents: g2Cents,
      gift3ThresholdCents: g3Cents,
      nextdayThresholdCents: nextdayCents,
      max1, max2, max3
    };
  }

  function calcFromCart(cart, ctx) {
    let subtotalExGifts = 0;
    let giftCount = 0;
    const giftVariantIds = new Set();
    const paidVariantIds = new Set();

    (cart.items || []).forEach(i => {
      const isGift = i.properties && i.properties._gift === 'true';
      if (isGift) {
        giftCount += i.quantity;
        giftVariantIds.add(i.variant_id);
      } else {
        paidVariantIds.add(i.variant_id);
        subtotalExGifts += i.final_line_price;
      }
    });

    const currency  = cart.currency || (window.Shopify && Shopify.currency && Shopify.currency.active) || 'GBP';
    const awayShip  = Math.max(ctx.shipThresholdCents - subtotalExGifts, 0);
    const awayG1    = Math.max(ctx.gift1ThresholdCents - subtotalExGifts, 0);
    const awayG2    = Math.max(ctx.gift2ThresholdCents - subtotalExGifts, 0);
    const awayG3    = Math.max(ctx.gift3ThresholdCents - subtotalExGifts, 0);

    const tier1     = subtotalExGifts >= ctx.gift1ThresholdCents;
    const tier2     = subtotalExGifts >= ctx.gift2ThresholdCents;
    const tier3     = subtotalExGifts >= ctx.gift3ThresholdCents;
    const nextday   = subtotalExGifts >= ctx.nextdayThresholdCents;

    const liveMax   = tier3 ? ctx.max3 : (tier2 ? ctx.max2 : (tier1 ? ctx.max1 : 0));
    const progTotal = Math.max(ctx.gift3ThresholdCents || 1, 1);
    const clamped   = Math.min(subtotalExGifts, progTotal);
    const pct       = (clamped / progTotal) * 100;

    const haveGift  = giftCount > 0;
    const atMax     = giftCount >= liveMax;

    return {
      subtotalExGifts, pct, currency,
      awayShip, awayG1, awayG2, awayG3,
      tier1, tier2, tier3, liveMax, nextday,
      haveGift, atMax, giftVariantIds,
      paidVariantIds
    };
  }

  // ---------- Section Rendering API: refresh gift bar + footer only ----------
  async function refreshQuickCartDOM() {
    const container = document.querySelector('.quick-cart');
    const sectionId = container && container.dataset.sectionId;
    if (!container || !sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);

    const res  = await fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' }});
    const data = await res.json();
    const html = data[sectionId] || data['quick-cart'];
    if (!html) return;

    const temp = document.createElement('div');
    temp.innerHTML = html;

    const replace = (sel) => {
      const src = temp.querySelector(sel);
      const dst = container.querySelector(sel);
      if (src && dst) {
        // Element exists — update its content in place
        dst.innerHTML = src.innerHTML;
      } else if (src && !dst) {
        // Element was wiped by the theme's own AJAX re-render — re-inject it.
        // .cart-progress must live before .quick-cart__items inside .quick-cart__main-content
        const anchor = container.querySelector('.quick-cart__items');
        if (anchor) {
          anchor.insertAdjacentElement('beforebegin', src.cloneNode(true));
        } else {
          // Fallback: prepend to main-content if items section not found
          const main = container.querySelector('.quick-cart__main-content');
          if (main) main.prepend(src.cloneNode(true));
        }
      }
    };

    // Only refresh the gift bar and footer.
    // Do NOT replace .quick-cart__header or .quick-cart__items —
    // those are owned by the theme's own AJAX handlers. Replacing them
    // destroys the theme's directly-bound event listeners (remove/qty buttons).
    replace('.cart-progress');
    replace('.quick-cart__footer');

    // carry the busy state into the fresh DOM instantly
    applyBusyState(container);

    // Re-bind carousel (guarded) and resync
    initGiftCarousel();
    debouncedSync();

    // Trigger theme event so cross-sells can re-init if needed
    document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
  }

  // ---------- add / remove ----------
  async function addGift(variantId) {
    // addGift is a genuine user-triggered write — interceptor should catch it
    await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: { _gift: 'true' }
      })
    });
  }

  async function removeGiftsIfNotEligible() {
    const ctx = getContext();
    if (!ctx) return;

    try {
      const cart  = await fetchCart();
      const state = calcFromCart(cart, ctx);

      if (state.tier1) {
        await trimGiftsToMax(cart, ctx);
        return;
      }

      // FIX #2: use internalCartFetch inside removeAllGiftsViaChange (see below)
      const removed = await removeAllGiftsViaChange(cart);
      if (removed) await refreshQuickCartDOM();
    } catch (err) {
      console.error('removeGiftsIfNotEligible failed:', err);
    } finally {
      debouncedSync();
    }
  }

  async function removeGiftsIfOnlyGiftsRemain() {
    try {
      const cart  = await fetchCart();
      const items = cart.items || [];
      if (!items.length) return;

      const hasPaid = items.some(i => !(i.properties && i.properties._gift === 'true') && i.final_line_price > 0);
      const hasGift = items.some(i => i.properties && i.properties._gift === 'true');

      if (hasPaid) return;

      if (hasGift) {
        const removed = await removeAllGiftsViaChange(cart);
        if (removed) await refreshQuickCartDOM();
      }
    } catch (err) {
      console.error('removeGiftsIfOnlyGiftsRemain failed:', err);
    }
  }

  // ---------- render ----------
  function renderProgress(ctx, state) {
    ctx.fillEl.style.width = `${Math.max(0, Math.min(100, state.pct))}%`;

    ctx.giftsWrap.dataset.max      = String(state.liveMax || 0);
    ctx.giftsWrap.dataset.eligible = state.tier1 ? 'true' : 'false';

    const nextdaySuffix = state.nextday ? ' + free next-day delivery' : '';

    let html;
    if (state.subtotalExGifts < ctx.shipThresholdCents) {
      html = `Only <strong>${formatMoneyIntl(state.awayShip, state.currency)}</strong> away from free shipping!`;
    } else if (!state.tier1) {
      html = `<strong>Free shipping unlocked</strong> • Only <strong>${formatMoneyIntl(state.awayG1, state.currency)}</strong> away from <strong>your first free gift</strong>`;
    } else if (!state.tier2) {
      html = `<strong>1 free gift unlocked</strong> — spend <strong>${formatMoneyIntl(state.awayG2, state.currency)}</strong> more to unlock <strong>more free gifts</strong>.`;
    } else if (!state.tier3) {
      // Tier 2 reached, working toward tier 3.
      // If next-day is already unlocked at this tier, celebrate it; otherwise tease it as part of the tier-3 reward.
      if (state.nextday) {
        html = `<strong>${ctx.max2} free gifts${nextdaySuffix} unlocked</strong> — spend <strong>${formatMoneyIntl(state.awayG3, state.currency)}</strong> more to unlock <strong>even more free gifts</strong>.`;
      } else {
        html = `<strong>${ctx.max2} free gifts unlocked</strong> — spend <strong>${formatMoneyIntl(state.awayG3, state.currency)}</strong> more to unlock <strong>even more free gifts + free next-day delivery</strong>.`;
      }
    } else {
      html = `<strong>You've unlocked ${state.liveMax} free gifts${nextdaySuffix}</strong> — choose up to ${state.liveMax}.`;
    }

    ctx.labelEl.innerHTML = html;

    // Update all gift pins by tier
    ctx.root.querySelectorAll('[data-gift-pin]').forEach(pin => {
      const tier = pin.getAttribute('data-gift-pin');
      let active = false;
      if (tier === '1')      active = state.tier1;
      else if (tier === '2') active = state.tier2;
      else if (tier === '3') active = state.tier3;
      pin.setAttribute('data-active', active ? 'true' : 'false');
    });

    const nextdayPin = ctx.root.querySelector('[data-nextday-pin]');
    if (nextdayPin) nextdayPin.setAttribute('data-active', state.nextday ? 'true' : 'false');
  }

  function renderGiftButtons(ctx, state) {
    if (giftsBusy) {
      $$('[data-add-gift]', ctx.giftsWrap).forEach(b => {
        b.disabled = true;
        b.classList.add('gift-is-disabled');
        b.setAttribute('aria-disabled', 'true');
      });
      return;
    }

    const btns = $$('[data-add-gift]', ctx.giftsWrap);
    btns.forEach(btn => {
      const vid          = Number(btn.getAttribute('data-variant'));
      const isGift       = state.giftVariantIds.has(vid);
      const isPaidInCart = state.paidVariantIds.has(vid);
      const card         = btn.closest('.cart-gifts__item');

      if (isPaidInCart && card) {
        card.style.display = 'none';
        return;
      } else if (card) {
        card.style.display = '';
      }

      const disable = !state.tier1 || state.atMax || isGift || !vid;

      btn.disabled = disable;
      btn.classList.toggle('gift-is-disabled', disable);
      if (disable) btn.setAttribute('aria-disabled', 'true');
      else         btn.removeAttribute('aria-disabled');

      let text = 'Add gift';
      if (!state.tier1)               text = 'Add gift (locked)';
      if (state.atMax && state.tier1) text = 'Limit reached';
      if (isGift)                     text = 'Added';
      if (btn.textContent.trim() !== text) btn.textContent = text;
    });
  }

  function hideCarouselIfAllInCart(ctx, state) {
    const cards        = $$('.cart-gifts__item', ctx.giftsWrap);
    const visibleCards = cards.filter(card => card.style.display !== 'none');
    if (cards.length > 0 && visibleCards.length === 0) {
      ctx.giftsWrap.style.display = 'none';
    } else {
      ctx.giftsWrap.style.display = '';
    }
  }

  // ---------- sync ----------
  async function syncUI() {
    const ctx = getContext();
    if (!ctx) {
      await removeGiftsIfOnlyGiftsRemain();
      return;
    }

    const cart  = await fetchCart();
    const state = calcFromCart(cart, ctx);

    renderProgress(ctx, state);
    renderGiftButtons(ctx, state);        // must run before hideCarouselIfAllInCart
    hideCarouselIfAllInCart(ctx, state);

    if (!state.tier1 && state.haveGift) {
      await removeGiftsIfNotEligible();
    }
  }

  const debouncedSync               = debounce(syncUI, 120);
  const debouncedEnforceBelowThresh = debounce(removeGiftsIfNotEligible, 140);
  const debouncedEnforceOnlyGifts   = debounce(removeGiftsIfOnlyGiftsRemain, 140);

  const schedulePostCartChangeChecks = (() => {
    const run = () => { debouncedEnforceOnlyGifts(); debouncedEnforceBelowThresh(); debouncedSync(); };
    return debounce(run, 80);
  })();

  // FIX #2: Interceptor now skips writes made by our own internal helpers
  try {
    const _fetch = window.fetch;
    window.fetch = function (...args) {
      const url = (typeof args[0] === 'string') ? args[0] : (args[0] && args[0].url);
      const isCartMutation = /\/cart\/(change|update|add)\.js(\?|$)/.test(url || '');
      const p = _fetch.apply(this, args);
      if (isCartMutation && !_internalWrite) {
        p.then(() => schedulePostCartChangeChecks()).catch(() => {});
      }
      return p;
    };
  } catch (e) {}

  function isGiftLine(i) { return i && i.properties && i.properties._gift === 'true'; }

  let lastSentMax = null;
  async function setGiftMaxAttribute(max) {
    const safe = max === null ? 0 : Math.max(0, Number(max) || 0);
    if (lastSentMax === safe) return;
    lastSentMax = safe;

    // FIX #2: use internalCartFetch so updating the attribute doesn't re-trigger cleanup
    if (safe === 0) {
      await internalCartFetch('/cart/update.js', { attributes: { _gift_max: '' } });
    } else {
      await internalCartFetch('/cart/update.js', { attributes: { _gift_max: String(safe) } });
    }
  }

  async function removeAllGiftsViaChange(cart) {
    const items    = (cart && cart.items) || [];
    const giftKeys = items.filter(isGiftLine).map(i => i.key);
    if (!giftKeys.length) return false;

    for (const key of giftKeys) {
      // FIX #2: use internalCartFetch so removal doesn't re-trigger schedulePostCartChangeChecks
      await internalCartFetch('/cart/change.js', { id: key, quantity: 0 });
    }

    await setGiftMaxAttribute(0);
    return true;
  }

  function scheduleEdgeCaseCleanupAndReload() {
    // FIX #5: only schedule one reload per page session
    if (_reloadScheduled) return;

    const run = async () => {
      try {
        const cart  = await fetchCart();
        const items = cart.items || [];
        if (!items.length) return;

        const hasPaid = items.some(i => !isGiftLine(i) && i.final_line_price > 0);
        const hasGift = items.some(isGiftLine);

        if (hasGift && !hasPaid) {
          const removed = await removeAllGiftsViaChange(cart);
          if (removed) {
            await refreshQuickCartDOM();
            if (!_reloadScheduled) {
              _reloadScheduled = true;
              setTimeout(() => { window.location.reload(); }, 1500);
            }
          }
        }
      } catch (e) {
        console.error('edgeCaseCleanup failed:', e);
      }
    };

    setTimeout(run, 120);
    setTimeout(run, 360);
    setTimeout(run, 900);
  }

  // ---------- pointer gesture helpers ----------
  let armX = 0, armY = 0;

  function clearArmedBusy() {
    const ctx = getContext();
    if (ctx) {
      ctx.giftsWrap.removeAttribute('data-busy');
      $$('[data-add-gift][data-busy-exempt]', ctx.giftsWrap)
        .forEach(b => b.removeAttribute('data-busy-exempt'));
    }
    giftAddLock = false;
    lockedBtn   = null;
  }

  document.addEventListener('pointerdown', (e) => {
    const btn = e.target.closest('[data-add-gift]');
    if (!btn) return;
    // FIX #6: was checking 'is-disabled' — corrected to 'gift-is-disabled'
    if (btn.disabled || btn.classList.contains('gift-is-disabled')) return;

    giftAddLock = true;
    lockedBtn   = btn;
    armX = e.clientX;
    armY = e.clientY;
  }, true);

  document.addEventListener('pointermove', (e) => {
    if (!lockedBtn) return;
    if (Math.abs(e.clientX - armX) > 8 || Math.abs(e.clientY - armY) > 8) {
      clearArmedBusy();
    }
  }, { passive: true });

  document.addEventListener('pointerup',     clearArmedBusy, true);
  document.addEventListener('pointercancel', clearArmedBusy, true);

  // ---------- events ----------
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-add-gift]');
    if (!btn) return;
    if (btn.disabled || giftsBusy) return;

    const variantId = btn.getAttribute('data-variant');
    if (!variantId) return;

    try {
      giftsBusy = true;
      applyBusyState();

      await addGift(variantId);

      const ctx = getContext();
      if (ctx) {
        const cart = await fetchCart();
        await trimGiftsToMax(cart, ctx);
      }

      await refreshQuickCartDOM();
      debouncedSync();
    } catch (err) {
      console.error(err);
    } finally {
      giftsBusy  = false;
      giftAddLock = false;
      lockedBtn  = null;
      applyBusyState();
      debouncedSync();
    }
  });

  document.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-remove-item]');
    if (!rm) return;

    setTimeout(() => {
      removeGiftsIfNotEligible();
      removeGiftsIfOnlyGiftsRemain();
      scheduleEdgeCaseCleanupAndReload();
      debouncedSync();
    }, 120);
  });

  ;(['cart:updated','cart:update','cart:change','ajax:cart:update','theme:cart:updated']).forEach(ev => {
    document.addEventListener(ev, () => {
      // If the theme's update wiped .cart-progress, re-inject before running checks
      const root = document.querySelector('.quick-cart');
      if (root && !root.querySelector('.cart-progress')) {
        refreshQuickCartDOM().then(() => {
          debouncedEnforceBelowThresh();
          debouncedEnforceOnlyGifts();
          debouncedSync();
        });
      } else {
        debouncedEnforceBelowThresh();
        debouncedEnforceOnlyGifts();
        scheduleEdgeCaseCleanupAndReload();
        debouncedSync();
      }
    });
  });

  // ---------- observers ----------
  let nodeObserver;
  function startNodeObserver() {
    if (nodeObserver) nodeObserver.disconnect();
    const root = $('.quick-cart');
    if (!root) return;
    nodeObserver = new MutationObserver(debounce(() => {
      // If the theme's own AJAX update wiped .cart-progress, re-inject it before syncing.
      if (!root.querySelector('.cart-progress')) {
        refreshQuickCartDOM().then(() => {
          initGiftCarousel();
          debouncedSync();
        });
      } else {
        debouncedSync();
        initGiftCarousel();
      }
    }, 120));
    nodeObserver.observe(root, { childList: true, subtree: true });
  }

  let docObserverStarted = false;
  function startDocObserver() {
    if (docObserverStarted) return;
    const docObserver = new MutationObserver(debounce(() => {
      if ($('.quick-cart')) {
        startNodeObserver();
        debouncedSync();
      }
    }, 120));
    docObserver.observe(document.documentElement, { childList: true, subtree: true });
    docObserverStarted = true;
  }

  // ---------- boot ----------
  document.addEventListener('DOMContentLoaded', () => {
    startDocObserver();
    startNodeObserver();
    initGiftCarousel();
    debouncedSync();
    debouncedEnforceBelowThresh();
    debouncedEnforceOnlyGifts();
  });

  setTimeout(() => { startDocObserver(); startNodeObserver(); debouncedSync(); }, 350);
})();
